<template>
  <div class="solfigr-strategy-comparison slfgrstracmp">
    <SolfigrStrategyComparisonBox
      :item="{ solution: totalSolution.solution, name: 'nodecompare_summary' }"
      :skeleton="skeleton"
      :showBetsIn="showBetsIn"
      :title="$t('common.summary')"
    />

    <StudyEqBucketsSwitcherNew
      :hasHelp="hasHelp"
      class="mb-2 z-index-1 mt-4"
      :showEquityBucketsOnly="isPreflop"
    />

    <div
      class="slfgrstracmp_items"
      @mouseleave="handleContMouseleave"
      @mouseenter="handleContMouseenter"
    >
      <SolfigrStrategyComparisonBox
        v-for="item in visibleCategories"
        :key="item.name"
        :item="item"
        :showBetsIn="showBetsIn"
        :skeleton="skeleton"
        @mouseenter="handleMouseenter(item)"
        @click="handleClick(item)"
      />
    </div>
  </div>
</template>

<script>
import { throttle } from 'lodash'
import { getGroupedActions, getSortedActions } from '@/helpers/grouping'
import { getRelativeSimilarity } from '@/helpers/rangeBuilder'
import StudyEqBucketsSwitcherNew from '@/components/Switchers/StudyEqBucketsSwitcherNew'
import SolfigrStrategyComparisonBox from '@/components/Dialogs/Solfigr/SolfigrStrategyComparisonBox'
import format_mix from '@/mixins/format_mix'

export default {
  name: 'SolfigrStrategyComparison',
  mixins: [format_mix],
  props: {
    isPreflop: Boolean,
    skeleton: Boolean,
    readOnly: Boolean,
    hasHelp: { type: Boolean, default: true },
    grouping: String,
    useHex: Boolean,
    showBetsIn: String,
    catDraws: { default: () => [] },
    catHands: { default: () => [] },
    catEqBuckets: { default: () => [] },
    catEqBucketsAdvanced: { default: () => [] },
    filterCategories: { default: () => [] },
    heroSolution: Array,
    opponentSolution: Array,
  },
 emits: ['cat:mouseenter','cat:mouseleave'],
  data() {
    return {
      mouseover: false,
    }
  },
  methods: {
    handleMouseenter: throttle(function (item) {
      if (this.readOnly) return
      if (!this.mouseover) return
      this.$emit('cat:mouseenter', item)
    }, 40),
    handleContMouseenter() {
      this.mouseover = true
    },
    handleContMouseleave() {
      if (this.readOnly) return
      this.mouseover = false
      this.$emit('cat:mouseleave')
    },
    handleClick(item) {},
    getVisibleCategories(categories) {
      let res = categories.map(i => {
        const {
          heroSolution, //
          opponentSolution,
          solution,
          matchPercent,
        } = this.getMergedSolution({
          heroSolution: i.players[0].solution,
          opponentSolution: i.players[1].solution,
        })

        const r = {
          ...i,
          heroSolution,
          opponentSolution,
          solution,
          matchPercent,
        }
        return r
      })
      res = res.filter(i => i.solution.length)
      // console.log(res)
      return res
    },
    getMergedSolution({ heroSolution, opponentSolution }) {
      heroSolution = !heroSolution
        ? null
        : getGroupedActions({
            actions: heroSolution || [],
            grouping: this.grouping,
            groupValues: ['total_frequency'],
            raisesFirst: true,
          })
      opponentSolution = !opponentSolution
        ? null
        : getGroupedActions({
            actions: opponentSolution || [],
            grouping: this.grouping,
            groupValues: ['total_frequency'],
            raisesFirst: true,
          })

      const mergedActions = getSortedActions({
        actions: [...heroSolution, ...opponentSolution],
        raisesFirst: true,
      })
      let allActions = mergedActions.map(aa => aa[['id', 'code'].find(k => k in aa)])
      allActions = [...new Set(allActions)]

      let solution = allActions.map(codeId => {
        const heroAction = heroSolution?.find(aa => aa[['id', 'code'].find(k => k in aa)] === codeId)
        const opponentAction = opponentSolution?.find(
          aa => aa[['id', 'code'].find(k => k in aa)] === codeId,
        )
        return {
          id: codeId,
          action: heroAction || opponentAction,
          heroAction: heroAction || {},
          opponentAction: opponentAction || {},
        }
      })

      const totalSimilar = solution.reduce(
        (a, b) =>
          a +
          getRelativeSimilarity({
            refNum: b.heroAction?.total_frequency ?? 999,
            newNum: b.opponentAction?.total_frequency ?? 999,
          }),
        0,
      )

      solution = solution.filter(
        aa =>
          this.numFixedUnit((aa.heroAction?.total_frequency || 0) * 100, 'strategy') ||
          this.numFixedUnit((aa.opponentAction?.total_frequency || 0) * 100, 'strategy'),
      )
      const res = {
        heroSolution,
        opponentSolution,
        solution,
        matchPercent: totalSimilar / (allActions.length || 1),
      }
      // console.log(res);
      return res
    },
  },
  computed: {
    totalSolution() {
      const {
        heroSolution, //
        opponentSolution,
        solution,
        matchPercent,
      } = this.getMergedSolution({
        heroSolution: this.heroSolution,
        opponentSolution: this.opponentSolution,
      })

      const res = {
        heroSolution,
        opponentSolution,
        solution,
        matchPercent,
      }

      // console.log(res)
      return res
    },
    hands() {
      return this.getVisibleCategories(this.catHands)
    },
    draws() {
      return this.getVisibleCategories(this.catDraws)
    },
    bucketsSimple() {
      return this.getVisibleCategories(this.catEqBuckets)
    },
    bucketsAdvanced() {
      return this.getVisibleCategories(this.catEqBucketsAdvanced)
    },
    visibleCategories() {
      switch (this.$store.getters.sw_range_compare) {
        case 'swv_eq_buckets_simple':
          return this.bucketsSimple
        case 'swv_eq_buckets_advanced':
          return this.bucketsAdvanced
        case 'swv_hands':
          return this.hands
        case 'swv_draws':
          return this.draws
        default:
          return []
      }
    },
    // styleEqBuckets() {
    // return this.$store.getters.mergedUserSettings.styleEqBuckets
    // },
  },
  components: {
    StudyEqBucketsSwitcherNew,
    SolfigrStrategyComparisonBox,
  },
}
</script>
<style lang="scss" scoped>
.slfgrstracmp {
  --gap: 0.25rem;

  display: flex;
  flex-direction: column;
  &_items {
    display: flex;
    flex-direction: column;
    gap: var(--gap);
  }
}
</style>
